"use client";

import { useEffect, useState } from "react";
import { TrendingUp, ChartBar as BarChart3, Users, Target } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Counter = {
  icon: "trending" | "chart" | "users" | "target";
  value: number;
  suffix: string;
  label: string;
};
const counters: Counter[] = [{
  icon: "trending",
  value: 45,
  suffix: "%",
  label: "Average Revenue Growth"
}, {
  icon: "chart",
  value: 120,
  suffix: "+",
  label: "Hotels Optimized"
}, {
  icon: "users",
  value: 98,
  suffix: "%",
  label: "Client Retention Rate"
}, {
  icon: "target",
  value: 25,
  suffix: "M",
  label: "Revenue Generated"
}];
function formatNumber(n: number): string {
  if (n >= 1_000_000) {
    const v = n / 1_000_000;
    return (v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)) + "M";
  }
  if (n >= 1_000) {
    const v = n / 1_000;
    return (v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)) + "K";
  }
  return n.toString();
}
function AnimatedCounter({
  target,
  isVisible
}: {
  target: number;
  isVisible: boolean;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const duration = 2500;
    const startTime = performance.now();
    let rafId = 0;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        setCount(target);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isVisible, target]);
  return <>{formatNumber(count)}</>;
}
export default function Strategicoutcomes() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const iconMap = {
    trending: TrendingUp,
    chart: BarChart3,
    users: Users,
    target: Target
  };
  return <section data-section-id="494" id="strategicoutcomes" className="bg-background text-foreground relative overflow-hidden py-24 md:py-40 px-4">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-30" style={{
      backgroundImage: "radial-gradient(ellipse 80% 50% at 20% 10%, hsla(222, 47%, 7%, 0.15), transparent 60%), radial-gradient(ellipse 60% 40% at 80% 30%, hsla(43, 65%, 35%, 0.12), transparent 60%), radial-gradient(circle at 50% 90%, hsla(222, 47%, 7%, 0.08), transparent 70%)"
    }} />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-20" style={{
      backgroundImage: "linear-gradient(115deg, transparent 40%, hsla(222, 47%, 7%, 0.08) 50%, transparent 60%), linear-gradient(75deg, transparent 30%, hsla(43, 65%, 35%, 0.04) 50%, transparent 70%)"
    }} />

      <div ref={ref} className="relative max-w-7xl mx-auto">
        {}
        <div className="text-center">
          <span className="font-default text-xs md:text-sm tracking-[0.3em] uppercase text-secondary">
            Proven Results
          </span>
        </div>

        {}
        <h2 className="font-default font-bold text-foreground text-center mt-6 text-4xl sm:text-5xl md:text-6xl leading-[1.05] max-w-4xl mx-auto">
          Data-Driven Revenue Excellence
        </h2>

        {}
        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-12 md:gap-12 relative">
          {counters.map((c, i) => {
          const Icon = iconMap[c.icon];
          return <div data-index={i} key={i} className="relative flex flex-col items-center text-center px-2 md:px-4">
                {}
                {i < counters.length - 1 && <span aria-hidden="true" className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-32 bg-border" />}

                <Icon className="mb-6 text-secondary" style={{
              width: "40px",
              height: "40px"
            }} strokeWidth={1.25} />

                <div className="flex items-baseline justify-center gap-2">
                  <span className="font-default font-bold text-foreground leading-none tracking-tight" style={{
                fontSize: "clamp(3rem, 8vw, 8rem)"
              }}>
                    <AnimatedCounter target={c.value} isVisible={isVisible} />
                  </span>
                  <span className="font-default text-base md:text-xl text-secondary">
                    {c.suffix}
                  </span>
                </div>

                <p className="mt-4 text-sm md:text-base text-foreground/70 font-default max-w-[14rem]">
                  {c.label}
                </p>
              </div>;
        })}
        </div>

        {}
        <div className="mt-24 text-center">
          <p className="font-default italic text-xl md:text-2xl text-primary">
            "Maximizing every booking opportunity through precision strategy."
          </p>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none" aria-hidden="true">
        <svg viewBox="0 0 1440 120" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-16 md:h-24">
          <defs>
            <linearGradient id="rippleWaveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="hsla(222, 47%, 7%, 0.15)" />
              <stop offset="50%" stopColor="hsla(43, 65%, 35%, 0.25)" />
              <stop offset="100%" stopColor="hsla(222, 47%, 7%, 0.15)" />
            </linearGradient>
          </defs>
          <path fill="url(#rippleWaveGradient)">
            <animate attributeName="d" dur="8s" repeatCount="indefinite" values="
                M0,64 C240,96 480,32 720,48 C960,64 1200,112 1440,80 L1440,120 L0,120 Z;
                M0,80 C240,48 480,96 720,72 C960,48 1200,64 1440,96 L1440,120 L0,120 Z;
                M0,64 C240,96 480,32 720,48 C960,64 1200,112 1440,80 L1440,120 L0,120 Z
              " />
          </path>
          <path fill="hsla(222, 47%, 7%, 0.08)">
            <animate attributeName="d" dur="10s" repeatCount="indefinite" values="
                M0,90 C360,60 720,100 1080,80 C1260,70 1380,85 1440,90 L1440,120 L0,120 Z;
                M0,80 C360,100 720,60 1080,90 C1260,105 1380,75 1440,80 L1440,120 L0,120 Z;
                M0,90 C360,60 720,100 1080,80 C1260,70 1380,85 1440,90 L1440,120 L0,120 Z
              " />
          </path>
        </svg>
      </div>
    </section>;
}